import React from 'react';
import cn from 'classnames';
import styles from './index.module.less';
import { IAddCartInstance } from '@base/product/useAddCart';
import { IOption, IVariant } from 'base/interfaces/product';
import FixedContainer from '@base/lib/FixedContainer';
import ProdSummary from '@base/product/ProdSummary';
import QtyBtns from '@app/product/AddToCart/QtyBtns';
import AddOns from '@app/product/AddToCart/AddOns';
import { ProdGifts } from '@pro/product/ProdGifts';
import { canAddToCart, canBuyNow } from 'base/utils/product';
import { useTrans } from 'base/utils/i18n';
import { ProdSoldOut } from 'mod/product/ProdSoldOut';
import { QuickViewOptions } from '@app/product/AddToCart/QuickViewOptions';

type AddToCartProps = {
    addCart: IAddCartInstance;
    qtyShown?: boolean;
    btnsShown?: boolean;
    isQuickView: boolean;
    shownVariant: IVariant;
    className?: string;
}

const AddToCart: React.FC<AddToCartProps> = props => {
    const {
        addCart,
        isQuickView,
        shownVariant,
        qtyShown = true,
        btnsShown = true,
        className,
    } = props;

    const {
        //groups
        groups,
        onCheckOption,
        // form
        isFormHidden,
        isErrorShown,
        outOfStock,
    } = addCart;

    const { addOns } = shownVariant;
    const { t } = useTrans();

    return !canAddToCart(shownVariant) && !canBuyNow(shownVariant) ? null : (
        <>
            <dl id="add-to-cart" className={className}>
                {!isFormHidden && groups.map((group, groupIndex) => (
                    <React.Fragment key={group.productOptionId}>
                        <dt className={styles['group-head']}>
                            <span className={styles.label}>{group.productOptionName}</span>
                            <span className={cn(
                                'text-danger ml-2',
                                styles['error-tip'],
                                { hide: !isErrorShown || group.checkedOption }
                            )}>
                                {t('web_product_not_choose_tip', { name: group.productOptionName })}
                            </span>
                        </dt>
                        <dd className={styles.options}>
                            {group.productOptionValues.map((option: IOption) => (
                                <span
                                    key={option.optionValueId}
                                    onClick={() => onCheckOption(groupIndex, option)}
                                    className={cn(
                                        styles.option,
                                        {
                                            [styles.checked]: group.checkedOption && group.checkedOption.optionValueId === option.optionValueId,
                                            [styles.out]: option.outOfStock,
                                            [styles.disabled]: option.disabled
                                        }
                                    )}
                                >
                                    {option.name}
                                </span>
                            ))}
                        </dd>
                    </React.Fragment>
                ))}
            </dl>

            <ProdGifts addCart={addCart}/>

            {isQuickView
                ? (
                    <QuickViewOptions
                        addCart={addCart}
                        qtyShown={qtyShown}
                        btnsShown={btnsShown}
                    />
                )
                : (
                    <FixedContainer className={cn(
                        styles['fixed-bar'],
                        addOns.length > 0 ? styles['with-addons'] : styles['without-addons']
                    )}>
                        <div className={styles.wrapper}>
                            {outOfStock ? (
                                <ProdSoldOut
                                    shownVariant={shownVariant}
                                    btnProps={{
                                        type: 'b'
                                    }}
                                    className={styles['sold-out']}
                                    popupProps={{
                                        className: styles['sold-out-popup'],
                                        btnProps: {
                                            type: 'b'
                                        }
                                    }}
                                />
                            ) : (
                                <>
                                    <ProdSummary
                                        addCart={addCart}
                                        className={styles.prices}
                                    />

                                    <div className={cn(styles['fixed-bar-options'], 'flex')}>
                                        <AddOns addCart={addCart}/>
                                        <QtyBtns addCart={addCart}/>
                                    </div>
                                </>
                            )}
                        </div>
                    </FixedContainer>
                )}
        </>
    );

};

export default AddToCart;
